import { PortalPhotoImportComponent, PortalPhotoSelection } from './portal-photo-import.component';
import { afterEveryRender, afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, inject, Injector, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UnitSelectorComponent } from '@shared/components';
import { ServiceEquivalenceSelectorComponent } from '@shared/components/service-equivalence-selector.component';
import { PortalMemberImportService, PersonPreview, Catalog, MemberSelection, ImportMemberResult, ImportPreviewField, MissingEquivalence, PortalImportEquivalence, SavePortalImportEquivalence } from '@core/api/services/portal-member-import.service';
import { PersonService } from '@features/people/services/person.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { map } from 'rxjs/operators';
import { concatMap, finalize, from, Observable, tap, timeout } from 'rxjs';
import { CreateMinistryAppointmentDto, MinistryAppointmentManagementService, MinistryAppointmentDto } from '../../services/ministry-appointment-management.service';
import { AssignmentDto, AssignmentManagementService } from '../../services/assignment-management.service';
import { ActivityConfigurationDto, ActivityManagementService } from '../../services/activity-management.service';
import { AreaDto, AreaManagementService } from '../../services/area-management.service';
import { MinistryManagementService } from '../../services/ministry-management.service';
import { ImportComparisonComponent, ImportComparisonRow } from './import-comparison.component';
import { ChurchImportComparisonComponent } from './church-import-comparison.component';
import { ServiceImportComparisonComponent } from './service-import-comparison.component';
import { MinistryImportComparisonComponent } from './ministry-import-comparison.component';
import { FamilyImportComparisonComponent } from './family-import-comparison.component';
import { PortalImportResultComponent } from './portal-import-result.component';
import { ContactImportComparisonComponent } from './contact-import-comparison.component';
import { QualityImportComparisonComponent, qualityImportRows, qualityNeedsReview } from './quality-import-comparison.component';

interface EquivalenceDraft { id?: number; sourceSection: string; sourceValue: string; targetKind: 'appointment' | 'assignment' | 'activity' | 'organizationUnit'; targetId?: number; targetReference?: string; activityRoleId?: number; ignore: boolean; unitRule: 'portal' | 'membership' | 'historical' | 'specific'; creatingTarget?: boolean; newTargetName?: string; newAreaId?: number; newOrganizationLevelIds?: number[]; newRequiresActiveMinistry?: boolean; }

@Component({ selector: 'app-portal-member-import', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PortalPhotoImportComponent, PortalImportResultComponent, CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatSelectModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatCheckboxModule, MatTabsModule, MatStepperModule, MatIconModule, MatProgressSpinnerModule, MatDialogModule, UnitSelectorComponent, ServiceEquivalenceSelectorComponent, ImportComparisonComponent, ChurchImportComparisonComponent, FamilyImportComparisonComponent, ContactImportComparisonComponent, MinistryImportComparisonComponent, ServiceImportComparisonComponent, QualityImportComparisonComponent],
  templateUrl: './portal-member-import.component.html', styleUrls: ['./portal-member-import.component.scss', './portal-import-members.scss', './portal-import-review.scss'] })
export class PortalMemberImportComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  selectedMemberTab = 0;
  pendingNavigationMessage = '';
  private pendingElement?: HTMLElement;
  private pendingLocation = '';
  private pendingNavigationVersion = 0;
  @ViewChild('createTargetDialog') createTargetDialog?: TemplateRef<unknown>;
  photoSelections: PortalPhotoSelection[] = [];
  photosLoading = false;
  sourceJson = ''; batchId?: string; fileName = ''; hasPreview = false;
  isCommunityJson = false;
  members: PersonPreview[] = []; catalogs: Catalog[] = []; communityOptionsList: { id: string; name: string }[] = []; selections: Record<string, MemberSelection> = {};
  result = ''; previewWarnings: string[] = []; memberResults: ImportMemberResult[] = []; loading = false; missingEquivalences: MissingEquivalence[] = []; equivalences: MissingEquivalence[] = [];
  importing = false;
  importProgress = { percent: 0, stage: 'Preparando importación', processedMembers: 0, totalMembers: 0 };
  get isPreparingImport(): boolean { return this.importProgress.stage.startsWith('Preparando importación'); }
  importSummary?: { created: number; skipped: number; warnings: string[] };
  equivalenceDrafts: Record<string, EquivalenceDraft> = {}; equivalenceSaving: Record<string, boolean> = {};
  equivalenceOriginals: Record<string, string> = {};
  activeEquivalenceKey?: string;
  activeCatalogKey?: string;
  initialEquivalenceCount = 0;
  activeStep = 1;
  reviewEquivalences: MissingEquivalence[] = [];
  reviewCatalogs: Catalog[] = [];
  reviewMembers: PersonPreview[] = [];
  membersStepReviewed = false;
  appointments: MinistryAppointmentDto[] = []; assignments: AssignmentDto[] = []; activities: ActivityConfigurationDto[] = [];
  appointmentOptions: { id: number; name: string }[] = []; assignmentOptions: { id: number; name: string }[] = []; activityOptions: { id: number; name: string }[] = [];
  serviceActivityRoles: { activityId: number; id: number; name: string; isDefault?: boolean }[] = [];
  ministryOptions: { id: number; name: string }[] = [];
  private ministriesLoaded = false;
  private configuredEquivalencesLoaded = false;
  private reviewChoicesInitialized = false;
  targetSearchControl = new FormControl('');
  showAllTargets = false;
  editingTarget = false;
  private readonly ministryUnitMappingsCache = new Map<string, { signature: string; mappings: Record<string, string> }>();
  areas: AreaDto[] = []; targetCreating = false;
  equivalenceError = ''; equivalenceSubmitting = false;
  creatingTargetItem?: MissingEquivalence;
  private createTargetDialogRef?: MatDialogRef<unknown>;
  selectedMemberId: string | null = null; importCommunityId?: string;
  editingImportCommunity = false;
  detectedImportCommunityId?: string;
  selectedImportCommunityName = '';
  communityDestinations: Record<string, string | undefined> = {};
  private photoUnitDestinationsCache = { signature: '', mappings: {} as Record<string, string> };
  private photoMemberReferencesCache = { signature: '', references: [] as string[] };
  get photoUnitDestinations(): Record<string, string> {
    const mappings: Record<string, string> = {};
    for (const draft of Object.values(this.equivalenceDrafts)) {
      if (draft.sourceSection === 'organizationUnits' && draft.targetKind === 'organizationUnit' && !draft.ignore && draft.targetReference)
        mappings[this.normalizeSourceValue(draft.sourceValue)] = draft.targetReference;
    }
    for (const catalog of this.catalogs) {
      if (catalog.kind === 'organizationUnit' && !catalog.ignore && catalog.targetId)
        mappings[this.normalizeSourceValue(catalog.source)] = catalog.targetId;
    }
    for (const [reference, destination] of Object.entries(this.communityDestinations)) {
      if (destination) mappings[this.normalizeSourceValue(reference)] = destination;
    }
    if (!this.hasMultipleCommunities && this.importCommunityId) {
      for (const member of this.members) {
        if (member.sourceCommunityReference) mappings[this.normalizeSourceValue(member.sourceCommunityReference)] = this.importCommunityId;
      }
    }
    const signature = JSON.stringify(mappings);
    if (this.photoUnitDestinationsCache.signature !== signature) this.photoUnitDestinationsCache = { signature, mappings };
    return this.photoUnitDestinationsCache.mappings;
  }
  get photoMemberReferences(): readonly string[] {
    const references = this.members.filter(member => this.selections[member.portalMemberId]?.include).map(member => member.portalMemberId);
    const signature = JSON.stringify(references);
    if (this.photoMemberReferencesCache.signature !== signature) this.photoMemberReferencesCache = { signature, references };
    return this.photoMemberReferencesCache.references;
  }
  get communityGroups(): { reference: string; name: string; count: number }[] {
    const groups = new Map<string, { reference: string; name: string; count: number }>();
    for (const member of this.members.filter(item => this.selections[item.portalMemberId]?.include)) {
      const reference = this.normalizeSourceValue(member.sourceCommunityReference || '');
      const group = groups.get(reference) || { reference, name: member.community || 'Comunidad de origen', count: 0 };
      group.count++; groups.set(reference, group);
    }
    return [...groups.values()];
  }
  trackCommunityGroup(_: number, group: { reference: string }): string { return group.reference; }
  get resultMemberCommunities(): Record<string, { id: string; name: string }> {
    return Object.fromEntries(this.members.map(member => {
      const destination = this.memberImportCommunity(member);
      const unit = this.communityOptionsList.find(item => item.id === destination);
      return [member.portalMemberId, { id: destination || member.sourceCommunityReference || '', name: unit?.name || member.community || 'Comunidad sin identificar' }];
    }));
  }
  hasMultipleCommunities = false;
  private memberImportCommunity(member: PersonPreview): string | undefined {
    return this.hasMultipleCommunities ? this.communityDestinations[this.normalizeSourceValue(member.sourceCommunityReference || '')] : this.importCommunityId;
  }
  get hasImportCommunities(): boolean {
    return this.members.filter(member => this.selections[member.portalMemberId]?.include).every(member => !!this.memberImportCommunity(member));
  }
  get importCommunityName(): string {
    return this.selectedImportCommunityName || this.communityOptionsList.find(unit => unit.id.toLowerCase() === this.importCommunityId?.toLowerCase())?.name || '';
  }
  restoringPersonId?: string;
  readonly isAdmin$: Observable<boolean>;

  constructor(
    private readonly api: PortalMemberImportService,
    private readonly personService: PersonService,
    private readonly userFullInfoService: UserFullInfoService,
    private readonly appointmentService: MinistryAppointmentManagementService,
    private readonly assignmentService: AssignmentManagementService,
    private readonly activityService: ActivityManagementService,
    private readonly areaService: AreaManagementService,
    private readonly ministryService: MinistryManagementService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {
    afterEveryRender(() => {
      const highlighted = this.host.nativeElement.querySelectorAll<HTMLElement>('.pending-navigation-target');
      if (!highlighted.length) return;
      const pending = new Set(this.pendingTargets());
      highlighted.forEach(element => {
        if (!pending.has(element)) element.classList.remove('pending-navigation-target');
      });
    });
    this.isAdmin$ = this.userFullInfoService.getUserInfo$().pipe(map(user => user?.isAdmin === true));
    this.ministryService.getAll().subscribe({
      next: items => { this.ministryOptions = items.filter(item => item.id != null).map(item => ({ id: item.id!, name: item.name || '' })); this.ministriesLoaded = true; this.initializeReviewChoices(); this.cdr.markForCheck(); },
      error: () => { this.ministriesLoaded = true; this.initializeReviewChoices(); this.cdr.markForCheck(); },
    });
  }

  async preview(event: Event): Promise<void> {
    if (this.loading) return;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.importSummary = undefined;
    this.selectedMemberTab = 0;
    this.pendingElement = undefined;
    this.pendingNavigationVersion++;
    this.photoSelections = []; this.members = []; this.catalogs = []; this.communityOptionsList = []; this.selections = {}; this.batchId = undefined; this.sourceJson = ''; this.hasPreview = false;
    this.result = ''; this.memberResults = []; this.previewWarnings = []; this.missingEquivalences = []; this.equivalences = []; this.equivalenceDrafts = {}; this.equivalenceSaving = {}; this.equivalenceOriginals = {}; this.equivalenceError = ''; this.activeEquivalenceKey = undefined; this.activeCatalogKey = undefined; this.initialEquivalenceCount = 0; this.activeStep = 1; this.membersStepReviewed = false; this.selectedMemberId = null; this.configuredEquivalencesLoaded = false; this.reviewChoicesInitialized = false; this.reviewEquivalences = []; this.reviewCatalogs = []; this.reviewMembers = [];
    this.importCommunityId = undefined; this.communityDestinations = {}; this.hasMultipleCommunities = false; this.detectedImportCommunityId = undefined; this.editingImportCommunity = false; this.selectedImportCommunityName = ''; this.fileName = file.name;
    this.isCommunityJson = false;
    if (!file.name.toLowerCase().endsWith('.json')) { this.result = 'Seleccioná una exportación JSON del Portal.'; return; }
    if (file.size > 100 * 1024 * 1024) { this.result = 'El archivo supera el límite de 100 MiB.'; return; }
    this.loading = true;
    this.cdr.markForCheck();
    try {
      this.sourceJson = await file.text();
      const source = JSON.parse(this.sourceJson);
      this.isCommunityJson = true;
      if (source.schema_version !== '3.4' || source.source !== 'NAC Portal' || !Array.isArray(source.communities) || !Array.isArray(source.members) || 'community' in source) {
        this.fail(null, 'Seleccioná una exportación NAC Portal 3.4 con las listas communities y members. Los formatos anteriores no se admiten.');
        return;
      }
      const sourceCommunities = new Map<string, string>();
      const sourceEventDates = new Map<string, { church: (string | undefined)[]; civil: (string | undefined)[] }>();
      const sourceHistoryDates = new Map<string, { start?: string; end?: string }[]>();
      if (this.isCommunityJson && Array.isArray(source.members)) {
        for (const member of source.members) {
          sourceHistoryDates.set(member.portal_id, Array.isArray(member.congregational_affiliation)
            ? member.congregational_affiliation.map((record: any) => ({
              start: this.portalEventDateText({ date: record.start_date, source_visible_data: { campos: { Fecha: record.source_visible_data?.campos?.Inicio } } }),
              end: this.portalEventDateText({ date: record.end_date, source_visible_data: { campos: { Fecha: record.source_visible_data?.campos?.Fin } } }),
            })) : []);
          sourceEventDates.set(member.portal_id, {
            church: Array.isArray(member.church_affiliation) ? member.church_affiliation.map(this.portalEventDateText) : [],
            civil: Array.isArray(member.family_status_events) ? member.family_status_events.map(this.portalEventDateText) : [],
          });
          const reference = member.congregation_portal_id;
          if (typeof member.portal_id === 'string' && typeof reference === 'string' && reference.trim())
            sourceCommunities.set(member.portal_id, `portal:${reference.trim()}`);
        }
      }
      let previewSubscription: { unsubscribe(): void } | undefined;
      const previewTimeoutMs = 135_000;
      const previewTimeout = window.setTimeout(() => {
        previewSubscription?.unsubscribe();
        this.fail(null, 'La previsualización tardó demasiado. Verificá que la API esté disponible y volvé a seleccionar el archivo.');
      }, previewTimeoutMs);
      previewSubscription = this.api.preview(file, this.isCommunityJson).pipe(
        timeout(previewTimeoutMs),
        finalize(() => {
          window.clearTimeout(previewTimeout);
          this.loading = false;
          this.cdr.markForCheck();
        }),
      ).subscribe({
        next: response => {
          window.clearTimeout(previewTimeout);
          this.members = (response.members ?? []).map(member => ({
            ...member,
            sourceCommunityReference: member.sourceCommunityReference ?? sourceCommunities.get(member.portalMemberId),
            events: (member.events ?? []).map(event => ({ ...event, rawDate: sourceEventDates.get(member.portalMemberId)?.church[event.index] ?? event.rawDate })),
            civilEvents: (member.civilEvents ?? []).map((event, index) => ({ ...event, rawDate: sourceEventDates.get(member.portalMemberId)?.civil[index] ?? event.rawDate })),
            communityHistory: (member.communityHistory ?? []).map(record => ({ ...record,
              rawStartDate: sourceHistoryDates.get(member.portalMemberId)?.[record.index]?.start,
              rawEndDate: sourceHistoryDates.get(member.portalMemberId)?.[record.index]?.end,
            })),
            emails: member.emails ?? [], phones: member.phones ?? [], addresses: member.addresses ?? [],
          })); this.catalogs = (response.catalogs ?? []).map(catalog => ({ ...catalog, ignore: false })); this.communityOptionsList = this.buildCommunityOptions(); this.batchId = response.batchId; this.hasPreview = true;
          this.hasMultipleCommunities = new Set(this.members.map(member => this.normalizeSourceValue(member.sourceCommunityReference || ''))).size > 1;
          this.previewWarnings = response.warnings ?? []; this.missingEquivalences = response.missingEquivalences ?? []; this.equivalences = response.equivalences ?? [...this.missingEquivalences]; this.initialEquivalenceCount = this.equivalences.length;
          this.configuredEquivalencesLoaded = false;
          this.createEquivalenceDrafts();
          if (this.isCommunityJson) { this.loadTargetOptions('appointment'); this.loadTargetOptions('assignment'); this.loadTargetOptions('activity'); }
          this.activeStep = 2;
          this.selections = Object.fromEntries(this.members.map(member => [member.portalMemberId, {
            include: true, status: 1, family: [], ministries: [], services: [],
            qualities: (member.sections?.find(section => section.name === 'Cualidades')?.rows ?? []).map((_, index) => ({ index, ignore: false })),
            events: [...(member.events ?? []).map((_, index) => ({ index, section: 'church' as const, include: true })), ...(member.civilEvents ?? []).map((_, index) => ({ index, section: 'civil' as const, include: true }))],
            communityHistory: (member.communityHistory ?? []).map(item => ({ index: item.index, community: item.community })),
            email: member.emails?.[0],
            phone: member.phones?.[0],
            address: member.addresses?.[0]
          }]));
          this.selectedMemberId = this.members[0]?.portalMemberId ?? null;
          for (const catalog of this.remainingCatalogs) {
            const matches = (catalog.options ?? []).filter(o => o.name.toLocaleLowerCase() === catalog.source.toLocaleLowerCase());
            if (matches.length === 1) catalog.targetId = matches[0].id;
          }
          this.cdr.markForCheck();
          if (response.configuredEquivalences) this.applyConfiguredEquivalences(response.configuredEquivalences);
          else this.loadConfiguredEquivalences();
        }, error: error => {
          window.clearTimeout(previewTimeout);
          const timedOut = error?.name === 'TimeoutError' || error?.status === 408 || error?.status === 504;
          this.fail(error, timedOut ? 'La previsualización tardó demasiado. Verificá que la API esté disponible y volvé a seleccionar el archivo.' : 'No se pudo previsualizar el archivo.');
        }
      });
    } catch { this.fail(null, 'No se pudo leer el JSON. Verificá que el archivo sea una exportación JSON válida del Portal.'); }
  }

  private portalEventDateText(record: {
    source_visible_data?: { campos?: { Fecha?: { texto?: string | null } } };
    date?: { day?: number | null; month?: number | null; year?: number | null } | null;
  }): string | undefined {
    const text = record.source_visible_data?.campos?.Fecha?.texto?.trim();
    if (text) return text;
    const date = record.date;
    if (!date || (date.day == null && date.month == null && date.year == null)) return undefined;
    const day = date.day == null ? '??' : String(date.day).padStart(2, '0');
    const month = date.month == null ? '??' : String(date.month).padStart(2, '0');
    const year = date.year == null ? '????' : String(date.year).padStart(4, '0');
    return `${day}.${month}.${year}`;
  }
  get selectedMemberCount(): number { return this.members.filter(m => this.selections[m.portalMemberId]?.include).length; }
  get validatedMemberCount(): number { return this.members.filter(member => this.selections[member.portalMemberId]?.include && !this.memberHasIncompleteChoices(member)).length; }
  get pendingReviewMemberCount(): number { return this.members.filter(member => this.memberHasPendingReview(member)).length; }
  get allMembersSelected(): boolean { return this.reviewMembers.length > 0 && this.reviewMembers.every(member => this.selections[member.portalMemberId]?.include); }
  get someMembersSelected(): boolean { return this.reviewMembers.some(member => this.selections[member.portalMemberId]?.include) && !this.allMembersSelected; }
  get remainingCatalogs(): Catalog[] { return this.catalogs.filter(c => c.kind !== 'organizationUnit'); }
  get activeMember(): PersonPreview | undefined { return this.reviewMembers.find(member => member.portalMemberId === this.selectedMemberId) ?? this.reviewMembers[0]; }
  memberDni(member: PersonPreview): string | undefined { return this.selections[member.portalMemberId]?.personalData?.documentNumber ?? member.details.find(detail => /^(documento|dni)$/i.test(detail.label.trim()))?.value; }
  readonly concurrenceStatuses = [{ value: 1, label: 'Activo/a' }, { value: 2, label: 'Inactivo/a' }, { value: 3, label: 'Se fue' }, { value: 4, label: 'Fallecido/a' }, { value: 5, label: 'Excluido/a' }];
  portalActivity(member: PersonPreview): string | undefined { return member.details.find(detail => detail.label === 'Actividad en el portal')?.value; }
  personalImportRows(member: PersonPreview, selection: MemberSelection): ImportComparisonRow[] {
    const value = (label: string) => member.details.find(item => item.label === label)?.value ?? '';
    const countryId = (label: string) => Number(this.catalogs.find(item => item.kind === 'country' && this.normalizeSourceValue(item.source) === this.normalizeSourceValue(value(label)))?.targetId) || null;
    const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const gender = normalize(value('Sexo'));
    const situation = normalize(value('Estado civil'));
    const data = selection.personalData ??= {
      gender: ['masculino', 'male', 'm'].includes(gender) ? 2 : ['femenino', 'female', 'f'].includes(gender) ? 1 : 3,
      relationshipSituation: situation.startsWith('casad') || situation === 'married' ? 2 : situation.startsWith('viud') || situation === 'widowed' ? 7 : situation.startsWith('divor') ? 6 : situation.startsWith('separ') ? 5 : situation.startsWith('solter') || situation === 'single' ? 1 : 0,
      documentNumber: member.details.find(detail => /^(documento|dni)$/i.test(detail.label.trim()))?.value,
      languages: [value('Idioma'), value('Otros idiomas')].filter(Boolean).join(', '),
      countryOfBirthId: countryId('País de nacimiento'), nationalityId: countryId('Nacionalidad')
    };
    const countries = [{ value: 0, label: '-' }, ...(this.catalogs.find(item => item.kind === 'country')?.options ?? []).map(item => ({ value: Number(item.id), label: item.name }))];
    const rows: ImportComparisonRow[] = member.details.filter(detail => !['Actividad en el portal'].includes(detail.label) && !(detail.label === 'Otros idiomas' && !!value('Idioma'))).map(detail => {
      const warning = member.warnings.find(item => this.warningMatchesPersonalField(item, detail.label))
        ?? this.incompleteDateWarning(member, detail.value)
        ?? (['País de nacimiento', 'Nacionalidad'].includes(detail.label) && this.personalCountryNeedsReview(member, detail.label)
          ? 'Elegí el valor equivalente en Comunidades.'
          : undefined);
      const row: ImportComparisonRow = { field: detail.label, portal: detail.value, communities: detail.value, mode: ['Correo SSO', 'Pastor del hogar (ID del Portal)', 'Foto'].includes(detail.label) ? 'excluded' : 'direct', warning };
      if (['Idioma', 'Otros idiomas'].includes(detail.label)) {
        row.field = 'Idiomas';
        row.portal = [value('Idioma'), value('Otros idiomas')].filter(Boolean).join(', ');
        row.editor = { value: data.languages, change: v => data.languages = v, maxLength: 200 };
      }
      if (/^(documento|dni)$/i.test(detail.label.trim())) row.editor = { value: data.documentNumber, change: v => data.documentNumber = v, maxLength: 50 };
      if (detail.label === 'Sexo') row.editor = { value: data.gender, options: [{ value: 1, label: 'Femenino' }, { value: 2, label: 'Masculino' }, { value: 3, label: 'Otro / sin especificar' }], change: v => data.gender = v };
      if (detail.label === 'Estado civil') row.editor = { value: data.relationshipSituation, options: [{ value: 0, label: 'Sin especificar' }, { value: 1, label: 'Soltero/a' }, { value: 2, label: 'Casado/a' }, { value: 3, label: 'Unión civil' }, { value: 4, label: 'Concubinato' }, { value: 5, label: 'Separado/a' }, { value: 6, label: 'Divorciado/a' }, { value: 7, label: 'Viudo/a' }], change: v => data.relationshipSituation = v };
      if (detail.label === 'País de nacimiento') row.editor = { value: data.countryOfBirthId ?? 0, options: countries, change: v => data.countryOfBirthId = v || null };
      if (detail.label === 'Nacionalidad') row.editor = { value: data.nationalityId ?? 0, options: countries, change: v => data.nationalityId = v || null };
      if (row.editor) row.mode = 'transformed';
      return row;
    });
    const activityWarning = member.warnings.find(item => this.warningMatchesPersonalField(item, 'Estado de concurrencia'));
    rows.push({ field: 'Estado de concurrencia', portal: this.portalActivity(member) || 'Sin dato', communities: '', mode: 'transformed', warning: activityWarning, status: { selection, options: this.isCommunityJson ? this.concurrenceStatuses.slice(0, 2) : this.concurrenceStatuses } });
    return rows;
  }
  sectionImportRows(member: PersonPreview, section: { name: string; rows: string[]; records?: ImportPreviewField[][] }): ImportComparisonRow[] {
    if (section.name === 'Historial de comunidad') {
      const selection = this.selections[member.portalMemberId];
      return (member.communityHistory ?? []).map(record => {
        const historySelection = selection.communityHistory.find(item => item.index === record.index) ?? { index: record.index };
        const selectedCommunity = historySelection.community?.trim();
        const missingCommunity = !selectedCommunity;
        const registered = !!selectedCommunity && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCommunity);
        return { field: section.name, portal: record.community ?? '', communities: '', mode: 'transformed', rule: 'Genera un pase de comunidad', history: {
          startDate: record.startDate,
          endDate: record.endDate,
          rawStartDate: record.rawStartDate,
          rawEndDate: record.rawEndDate,
          startDateWarning: !record.startDate && !historySelection.startDate ? 'La fecha de inicio está incompleta o no se conoce. Ingresá una fecha completa para importar este pase.' : undefined,
          endDateWarning: record.rawEndDate?.includes('?') ? 'La fecha de fin del Portal está incompleta.' : undefined,
          birthDate: this.eventBirthDate(member),
          selection: historySelection,
          warning: missingCommunity ? 'Falta seleccionar una comunidad o ingresar un nombre hist\u00F3rico.' : undefined,
          status: missingCommunity ? 'Falta seleccionar una comunidad o ingresar un nombre hist\u00F3rico.' : registered ? 'Comunidad registrada' : undefined,
          registered
        } };
      });
    }
    if (section.name === 'Cualidades') {
      const selection = this.selections[member.portalMemberId];
      return section.rows.map((row, index) => {
        const fields = section.records?.[index];
        const name = fields?.find(field => field.label === 'Cualidad')?.value ?? row;
        const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ');
        const target = normalized.includes('acuerdo de confidencialidad')
          ? 'Acuerdo de confidencialidad'
          : (normalized.includes('ficha personal') || normalized.includes('f personal') || normalized.includes('f.personal')) && normalized.includes('firmada') && (normalized.includes('administracion') || normalized.includes('adm'))
            ? 'Ficha firmada en Administración Central'
            : 'Otras cualidades';
        const importSelection = selection.qualities.find(item => item.index === index) ?? { index, ignore: false };
        if (!selection.qualities.some(item => item.index === index)) selection.qualities.push(importSelection);
        const sourceWarning = member.warnings.find(warning => {
          const date = warning.match(/fecha incompleta \((.*?)\)/i)?.[1];
          return warning.startsWith('Cualidades:') && !!date && row.includes(date);
        });
        const warning = !importSelection.ignore && sourceWarning
          ? `Fecha incompleta (${sourceWarning.match(/fecha incompleta \((.*?)\)/i)?.[1]}). Esta cualidad no se puede importar con una fecha incompleta. Podés omitirla.` : undefined;
        return { field: section.name, portal: row, communities: target, mode: 'direct' as const,
          importSelection, warning, destinationFields: fields?.map(field => ({ label: field.label, value: field.value || '—' })) ?? [{ label: 'Cualidad', value: row }] };
      });
    }
    const pending = member.notImportedSections.includes(section.name);
    return section.rows.map(row => ({ field: section.name, portal: row, communities: pending ? 'No se importa todavía' : 'Se procesa en Comunidades', mode: pending ? 'excluded' : 'transformed', rule: pending ? undefined : 'Se procesa como registro de ' + section.name.toLocaleLowerCase() + '.' }));
  }
  familySection(member: PersonPreview) { return member.sections.find(section => section.name === 'Familia'); }
  qualityRows(member: PersonPreview, section: { name: string; rows: string[]; records?: ImportPreviewField[][] }) {
    return qualityImportRows(section, this.selections[member.portalMemberId].qualities);
  }
  visibleSections(member: PersonPreview) { return member.sections.filter(section => section.name !== 'Historia civil' && section.name !== 'Estado civil' && section.name !== 'Familia' && section.name !== 'Acontecimientos'); }
  communityOptions(): { id: string; name: string }[] { return this.communityOptionsList; }
  ministryUnitMappings(member: PersonPreview): Record<string, string> {
    const drafts = Object.values(this.equivalenceDrafts).filter(draft => draft.sourceSection === 'organizationUnits');
    const unitCatalogs = this.catalogs.filter(catalog => catalog.kind === 'organizationUnit' && catalog.targetId);
    const signature = JSON.stringify([this.memberImportCommunity(member), drafts.map(draft => [draft.sourceValue, draft.ignore, draft.targetReference]), unitCatalogs.map(catalog => [catalog.source, catalog.targetId])]);
    const cached = this.ministryUnitMappingsCache.get(member.portalMemberId);
    if (cached?.signature === signature) return cached.mappings;
    const mappings: Record<string, string> = {};
    for (const draft of drafts) if (!draft.ignore && draft.targetReference) mappings[draft.sourceValue.trim().toLowerCase()] = draft.targetReference;
    for (const catalog of unitCatalogs) mappings[catalog.source.trim().toLowerCase()] = catalog.targetId!;
    const origin = member.details.find(item => item.label === 'Comunidad de origen (ADM)')?.value;
    const destination = this.memberImportCommunity(member);
    if (origin && destination) mappings[origin.trim().toLowerCase()] ??= destination;
    this.ministryUnitMappingsCache.set(member.portalMemberId, { signature, mappings });
    return mappings;
  }
  private buildCommunityOptions(): { id: string; name: string }[] {
    return this.catalogs.find(catalog => catalog.kind === 'organizationUnit')?.options ?? [];
  }
  selectedHistoryCommunity(selection: MemberSelection, index: number): string | null { return selection.communityHistory.find(item => item.index === index)?.community ?? null; }
  setHistoryCommunity(selection: MemberSelection, index: number, community: string | null): void {
    const item = selection.communityHistory.find(value => value.index === index);
    if (item) item.community = community ?? undefined;
  }
  memberTabNeedsReview(member: PersonPreview, tab: string): boolean {
    if (tab === 'Servicios') return this.serviceSectionNeedsReview(member, 'designations');
    if (tab === 'Colaboraciones') return this.serviceSectionNeedsReview(member, 'participations');
    if (tab === 'Ministerios') return this.ministrySectionNeedsReview(member);
    if (tab === 'Cualidades') {
      const section = member.sections.find(section => section.name === tab);
      return !!section && this.qualityRows(member, section).some(qualityNeedsReview);
    }
    const warnings = member.warnings.map(warning => warning.toLocaleLowerCase());
    if (tab === 'Historial de comunidad') return this.communityHistoryNeedsReview(member);
    if (tab === 'Historia civil') return this.eventDatesNeedReview(member);
    if (tab === 'Estado civil') return this.memberTabNeedsReview(member, 'Datos personales');
    if (tab === 'Contacto') return (['email', 'phone', 'address'] as const).some(field => this.memberFieldNeedsReview(member, field));
    if (tab === 'Familia') return false;
    if (tab === 'Datos personales') return member.details.some(detail => member.warnings.some(warning => this.warningMatchesPersonalField(warning, detail.label)))
      || member.warnings.some(warning => this.warningMatchesPersonalField(warning, 'Estado de concurrencia') || /información personal|estado civil/i.test(warning))
      || this.personalCountryNeedsReview(member, 'País de nacimiento')
      || this.personalCountryNeedsReview(member, 'Nacionalidad');
    if (tab === 'Iglesia') return this.eventDatesNeedReview(member);
    return !/direcciones/i.test(tab) && warnings.some(warning => warning.includes(tab.toLocaleLowerCase()));
  }
  private eventDatesNeedReview(member: PersonPreview): boolean {
    const selections = this.selections[member.portalMemberId]?.events ?? [];
    const needsReview = (event: { date?: string | null; rawDate?: string | null; issue?: string | null }, index: number, section: 'church' | 'civil'): boolean => {
      const selection = selections.find(item => item.index === index && item.section === section);
      if (selection?.include === false) return false;
      const date = selection?.date ?? event.date;
      if (!date || date > new Date().toLocaleDateString('sv-SE') || (this.eventBirthDate(member) && date < this.eventBirthDate(member)!)) return true;
      if (event.issue && !(selection?.date && /fecha/i.test(event.issue))) return true;
      return false;
    };
    return member.events.some((event, index) => event.isImportable !== false && needsReview(event, index, 'church'))
      || !!member.civilEvents?.some((event, index) => !!event.targetEventType && needsReview(event, index, 'civil'));
  }
  eventBirthDate(member: PersonPreview): string | undefined {
    const value = member.details.find(detail => detail.label === 'Nacimiento' || detail.label === 'Fecha de nacimiento')?.value;
    return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
  }
  private personalCountryNeedsReview(member: PersonPreview, field: string): boolean {
    const sourceValue = member.details.find(detail => detail.label === field)?.value?.trim();
    if (!sourceValue) return false;
    const catalog = this.catalogs.find(catalog => catalog.kind === 'country' && this.normalizeSourceValue(catalog.source) === this.normalizeSourceValue(sourceValue));
    if (catalog?.ignore) return false;
    const selection = this.selections[member.portalMemberId]?.personalData;
    const selectedValue = field === 'País de nacimiento' ? selection?.countryOfBirthId : selection?.nationalityId;
    if (selection) return !selectedValue;
    return !catalog?.targetId;
  }
  private incompleteDateWarning(member: PersonPreview, value: string): string | undefined {
    if (!value) return undefined;
    return member.warnings.find(warning => /fecha incompleta/i.test(warning) && warning.includes(`(${value})`));
  }
  private communityHistoryNeedsReview(member: PersonPreview): boolean {
    const selected = this.selections[member.portalMemberId]?.communityHistory ?? [];
    return (member.communityHistory ?? []).some(record => {
      const selection = selected.find(item => item.index === record.index);
      return !selection?.community?.trim() || !(selection?.startDate || record.startDate) || !!record.rawEndDate?.includes('?');
    });
  }
  private warningMatchesPersonalField(warning: string, field: string): boolean {
    const message = warning.toLocaleLowerCase();
    const label = field.toLocaleLowerCase();
    if (message.startsWith('falta el identificador')) return label.includes('adm') || label.includes('documento');
    if (message.includes('nombre o apellido')) return label === 'nombre' || label === 'apellido';
    if (message.includes('sexo no reconocido')) return label === 'sexo';
    if (message.includes('estado de actividad no reconocido')) return label.includes('concurrencia') || label.includes('actividad');
    return false;
  }
  private serviceSectionNeedsReview(member: PersonPreview, section: 'designations' | 'participations'): boolean {
    const sectionName = section === 'designations' ? 'Servicios' : 'Colaboraciones';
    const records = member.sections.find(item => item.name === sectionName)?.records ?? [];
    const selections = this.selections[member.portalMemberId]?.services ?? [];
    const unitMappings = this.ministryUnitMappings(member);
    return records.some((record, index) => {
      const selected = selections.find(item => item.section === section && item.index === index);
      if (selected) return !selected.ignore && (!selected.targetId || selected.unitRule === 'specific' && !selected.targetReference);

      const sourceValue = record.find(field => field.label === 'Servicio')?.value ?? '';
      const draft = this.equivalenceDrafts[`${section}|${sourceValue}`];
      if (draft?.ignore) return false;
      if (!draft?.targetId) return true;

      const sourceUnit = this.sourceUnitKey(record);
      const sourceUnitName = record.find(field => field.label === 'Unidad')?.value;
      const mappedUnit = (sourceUnit ? unitMappings[sourceUnit.trim().toLowerCase()] : undefined)
        ?? (!sourceUnit?.startsWith('portal:') && sourceUnitName ? unitMappings[sourceUnitName.trim().toLowerCase()] : undefined);
      const unitEquivalence = (sourceUnit || sourceUnitName) ? this.equivalenceDrafts[`organizationUnits|${sourceUnit || sourceUnitName}`] : undefined;
      const historical = !mappedUnit && unitEquivalence?.ignore === true;
      const unitRule = historical ? 'historical' : mappedUnit ? 'specific' : draft.unitRule;
      const targetReference = mappedUnit ?? draft.targetReference;
      return unitRule === 'specific' && !targetReference;
    });
  }
  private ministrySectionNeedsReview(member: PersonPreview): boolean {
    const section = member.sections.find(item => item.name === 'Ministerios');
    const records = section?.records ?? [];
    const importedRecords = section?.importedRecords ?? [];
    const selections = this.selections[member.portalMemberId]?.ministries ?? [];
    return records.some((record, index) => {
      const selected = selections.find(item => item.index === index);
      if (selected) return !selected.ignore && (!selected.eventType || !selected.ministryId);
      if (!this.ministriesLoaded) return true;

      const imported = importedRecords[index] ?? [];
      const event = imported.find(item => item.label === 'Acontecimiento ministerial')?.value ?? '';
      const eventType = this.importedMinistryEventType(event);
      const ministryName = imported.find(item => item.label === 'Nivel de ministerio')?.value
        || record.find(item => item.label === 'Nivel de ministerio')?.value
        || '';
      const ministryId = this.ministryOptions.find(item => this.normalizeSourceValue(item.name) === this.normalizeSourceValue(ministryName))?.id;
      return !eventType || !ministryId;
    });
  }
  private initializeReviewChoices(): void {
    if (this.reviewChoicesInitialized || this.activeStep !== 3 || !this.ministriesLoaded || !this.configuredEquivalencesLoaded || !this.members.length) return;
    this.inferImportCommunity();
    for (const member of this.members) {
      const selection = this.selections[member.portalMemberId];
      const unitMappings = this.ministryUnitMappings(member);
      for (const record of member.communityHistory ?? []) {
        let historySelection = selection.communityHistory.find(item => item.index === record.index);
        if (!historySelection) {
          historySelection = { index: record.index };
          selection.communityHistory.push(historySelection);
        }
        const equivalence = record.sourceCommunityMdmNo
          ? this.equivalences.find(item => item.sourceSection === 'organizationUnits'
            && this.normalizeSourceValue(item.sourceValue) === this.normalizeSourceValue(record.sourceCommunityMdmNo!))
          : undefined;
        const unitEquivalence = equivalence ? this.draftFor(equivalence) : undefined;
        if (unitEquivalence?.targetKind === 'organizationUnit' && !unitEquivalence.ignore && unitEquivalence.targetReference)
          historySelection.community = unitEquivalence.targetReference;
        else historySelection.community ??= record.community;
      }
      for (const [sectionName, sectionKey] of [['Servicios', 'designations'], ['Colaboraciones', 'participations']] as const) {
        const records = member.sections.find(item => item.name === sectionName)?.records ?? [];
        for (const [index, record] of records.entries()) {
          let selected = selection.services.find(item => item.section === sectionKey && item.index === index);
          if (selected) continue;
          const sourceValue = record.find(field => field.label === 'Servicio')?.value ?? '';
          const draft = this.findEquivalenceDraft(sectionKey, sourceValue);
          const sourceUnit = this.sourceUnitKey(record);
          const sourceUnitName = record.find(field => field.label === 'Unidad')?.value;
          const mappedUnit = (sourceUnit ? unitMappings[sourceUnit.trim().toLowerCase()] : undefined)
            ?? (!sourceUnit?.startsWith('portal:') && sourceUnitName ? unitMappings[sourceUnitName.trim().toLowerCase()] : undefined);
          const unitEquivalence = (sourceUnit || sourceUnitName) ? this.findEquivalenceDraft('organizationUnits', sourceUnit || sourceUnitName!) : undefined;
          const historical = !mappedUnit && unitEquivalence?.ignore === true;
          selection.services.push({ section: sectionKey, index, ignore: draft?.ignore ?? false, targetKind: draft?.targetKind ?? 'activity', targetId: draft?.targetId, activityRoleId: draft?.activityRoleId, unitRule: historical ? 'historical' : mappedUnit ? 'specific' : draft?.unitRule ?? 'portal', targetReference: mappedUnit ?? draft?.targetReference });
        }
      }
      const ministrySection = member.sections.find(item => item.name === 'Ministerios');
      const records = ministrySection?.records ?? [];
      const importedRecords = ministrySection?.importedRecords ?? [];
      for (const [index, record] of records.entries()) {
        let selected = selection.ministries.find(item => item.index === index);
        if (!selected) {
          const imported = importedRecords[index] ?? [];
          const event = imported.find(item => item.label === 'Acontecimiento ministerial')?.value ?? '';
          const eventType = this.importedMinistryEventType(event);
          const area = imported.find(item => item.label === 'Área de actividad');
          selected = { index, ignore: false, eventType, community: area?.sourceReference ? unitMappings[area.sourceReference.trim().toLowerCase()] : area?.value || undefined,
            location: imported.find(item => item.label === 'Lugar')?.value, notes: imported.find(item => item.label === 'Observaciones')?.value,
            expectedEndDate: imported.find(item => item.label === 'Fecha prevista de fin')?.value || null };
          selection.ministries.push(selected);
        }
        if (!selected.ministryId) {
          const imported = importedRecords[index] ?? [];
          const ministryName = imported.find(item => item.label === 'Nivel de ministerio')?.value
            || record.find(item => item.label === 'Nivel de ministerio')?.value
            || '';
          selected.ministryId = this.ministryOptions.find(item => this.normalizeSourceValue(item.name) === this.normalizeSourceValue(ministryName))?.id;
        }
      }
    }
    this.reviewChoicesInitialized = true;
    this.reviewMembers = this.members.filter(member => this.memberHasIncompleteChoices(member));
    this.selectedMemberId = this.reviewMembers[0]?.portalMemberId ?? null;
    if (!this.reviewMembers.length) this.goToReview();
    this.cdr.markForCheck();
  }
  memberFieldNeedsReview(member: PersonPreview, field: 'email' | 'phone' | 'address'): boolean {
    const selection = this.selections[member.portalMemberId];
    const pattern = field === 'email' ? /correo|email/ : field === 'phone' ? /teléfono|telefono/ : /dirección|direccion/;
    const values = field === 'email' ? member.emails : field === 'phone' ? member.phones : member.addresses;
    const requiresDecision = values.length > 0 || member.warnings.some(warning => pattern.test(warning.toLocaleLowerCase()));
    return requiresDecision && selection?.[field] === undefined;
  }
  memberHasPendingReview(member: PersonPreview): boolean {
    if (!this.selections[member.portalMemberId]?.include) return false;
    return this.memberTabNeedsReview(member, 'Datos personales')
      || this.memberTabNeedsReview(member, 'Contacto')
      || this.memberTabNeedsReview(member, 'Familia')
      || this.memberTabNeedsReview(member, 'Iglesia')
      || member.sections.some(section => this.memberTabNeedsReview(member, section.name));
  }
  private memberHasIncompleteChoices(member: PersonPreview): boolean {
    const selection = this.selections[member.portalMemberId];
    if (!selection?.include) return false;
    return this.memberHasPendingReview(member)
      || (['email', 'phone', 'address'] as const).some(field => {
        const values = field === 'email' ? member.emails : field === 'phone' ? member.phones : member.addresses;
        return values.length > 0 && selection[field] === undefined;
      })
      || selection.family.some(item => item.associate && !item.personId)
      || selection.ministries.some(item => !item.ignore && (!item.eventType || !item.ministryId))
      || selection.services.some(item => !item.ignore && (!item.targetId || item.unitRule === 'specific' && !item.targetReference))
      || this.ministrySectionNeedsReview(member)
      || this.serviceSectionNeedsReview(member, 'designations')
      || this.serviceSectionNeedsReview(member, 'participations');
  }
  get resolvedEquivalenceCount(): number {
    return this.reviewEquivalences.filter(item => this.isEquivalenceValid(item)).length
      + this.reviewCatalogs.filter(catalog => catalog.ignore || !!catalog.targetId).length;
  }
  get importReadiness(): string[] {
    const pending: string[] = [];
    if (this.missingEquivalences.length) pending.push(`${this.missingEquivalences.length} equivalencia${this.missingEquivalences.length === 1 ? '' : 's'} pendiente${this.missingEquivalences.length === 1 ? '' : 's'}`);
    if (this.hasIncompleteChoices) pending.push('completá las elecciones en Comunidades');
    if (!this.selectedMemberCount) pending.push('seleccioná al menos una persona');
    if (!this.hasImportCommunities) pending.push('elegí la comunidad de destino para cada comunidad de origen');
    if (this.photosLoading) pending.push('esperá mientras se verifican las fotos');
    return pending;
  }
  get hasIncompleteChoices(): boolean {
    return this.members.some(member => this.memberHasIncompleteChoices(member));
  }
  get canImport(): boolean { return !this.hasIncompleteChoices && !this.loading && !this.photosLoading && this.missingEquivalences.length === 0 && this.selectedMemberCount > 0 && this.hasImportCommunities && !!this.batchId; }
  get membersStepCompleted(): boolean { return this.membersStepReviewed && this.hasPreview && this.members.length > 0 && this.missingEquivalences.length === 0 && this.selectedMemberCount > 0 && !this.hasIncompleteChoices; }
  get currentStep(): number { return this.activeStep; }
  selectStep(selectedIndex: number): void {
    const step = selectedIndex + 1;
    if (step === 1 || (step === 2 && this.hasPreview && (this.reviewEquivalences.length || this.reviewCatalogs.length)) || (step === 3 && this.hasPreview && this.reviewMembers.length && !this.missingEquivalences.length) || (step === 4 && this.membersStepCompleted)) this.activeStep = step;
    if (this.activeStep === 3) this.initializeReviewChoices();
    this.cdr.markForCheck();
  }
  goToReview(): void {
    if (this.hasIncompleteChoices) {
      const member = this.members.find(item => this.selections[item.portalMemberId]?.include && this.memberHasPendingReview(item));
      if (member) { this.selectedMemberId = member.portalMemberId; this.activeStep = 3; }
      this.membersStepReviewed = false;
      this.cdr.markForCheck();
      return;
    }
    this.membersStepReviewed = true; this.activeStep = 4; this.cdr.markForCheck();
  }
  toggleAllMembers(include: boolean): void { this.reviewMembers.forEach(m => this.selections[m.portalMemberId].include = include); }
  selectMember(member: PersonPreview): void {
    this.selectedMemberId = member.portalMemberId;
    this.selectedMemberTab = 0;
    this.pendingElement = undefined;
    this.pendingNavigationVersion++;
  }
  @HostListener('document:keydown', ['$event'])
  navigatePendingShortcut(event: KeyboardEvent): void {
    if (!event.altKey || !event.ctrlKey || event.metaKey || event.shiftKey || (this.activeStep !== 2 && this.activeStep !== 3) || this.loading || this.importing || this.createTargetDialogRef || this.equivalenceSubmitting || this.targetCreating) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    if (this.activeStep === 2) { this.navigatePendingEquivalence(event.key === 'ArrowDown' ? 1 : -1); return; }
    if (!this.pendingReviewMemberCount) return;
    this.navigatePending(event.key === 'ArrowDown' ? 1 : -1);
  }
  navigatePending(direction: 1 | -1): void {
    const locations = this.reviewMembers.flatMap(member => {
      if (!this.selections[member.portalMemberId]?.include) return [];
      const tabs = ['Datos personales', 'Contacto'];
      if (this.familySection(member)) tabs.push('Familia');
      if (member.events.length || member.civilEvents?.length) tabs.push('Iglesia');
      tabs.push(...this.visibleSections(member).map(section => section.name));
      return tabs.map((name, index) => ({ member, name, index }))
        .filter(tab => this.memberTabNeedsReview(member, tab.name));
    });
    if (!locations.length) return;
    const current = locations.findIndex(location => location.member.portalMemberId === this.selectedMemberId && location.index === this.selectedMemberTab);
    const currentKey = `${this.selectedMemberId}:${this.selectedMemberTab}`;
    if (current >= 0) {
      const targets = this.pendingTargets();
      const anchor = this.pendingLocation === currentKey ? this.pendingElement : undefined;
      const candidate = anchor?.isConnected
        ? (direction === 1 ? targets : [...targets].reverse()).find(target => target !== anchor && !!(anchor.compareDocumentPosition(target) & (direction === 1 ? Node.DOCUMENT_POSITION_FOLLOWING : Node.DOCUMENT_POSITION_PRECEDING)))
        : direction === 1 ? targets[0] : targets[targets.length - 1];
      if (candidate) { this.focusPending(candidate, currentKey, locations[current].member.fullName); return; }
    }
    const memberIndex = this.reviewMembers.findIndex(member => member.portalMemberId === this.selectedMemberId);
    const orderedLocations = locations.map((location, index) => ({ ...location, position: index, memberIndex: this.reviewMembers.indexOf(location.member) }));
    const nextLocation = (direction === 1 ? orderedLocations : [...orderedLocations].reverse()).find(location => direction === 1
      ? location.memberIndex > memberIndex || location.memberIndex === memberIndex && location.index > this.selectedMemberTab
      : location.memberIndex < memberIndex || location.memberIndex === memberIndex && location.index < this.selectedMemberTab);
    const index = current < 0 ? (nextLocation?.position ?? (direction === 1 ? 0 : locations.length - 1)) : (current + direction + locations.length) % locations.length;
    const destination = locations[index];
    this.selectedMemberId = destination.member.portalMemberId;
    this.selectedMemberTab = destination.index;
    const version = ++this.pendingNavigationVersion;
    this.cdr.markForCheck();
    afterNextRender(() => {
      if (version !== this.pendingNavigationVersion) return;
      const targets = this.pendingTargets();
      const target = direction === 1 ? targets[0] : targets[targets.length - 1];
      this.host.nativeElement.querySelector('.member-card--selected')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (target) this.focusPending(target, `${destination.member.portalMemberId}:${destination.index}`, destination.member.fullName);
    }, { injector: this.injector });
  }
  private pendingTargets(): HTMLElement[] {
    const panel = this.host.nativeElement.querySelector<HTMLElement>('.person-detail .mat-mdc-tab-body-active');
    if (!panel) return [];
    const targets = Array.from(panel.querySelectorAll<HTMLElement>('.row-warning, .history-warning, .service-warning, [data-import-pending]'))
      .filter(marker => {
        const details = marker.closest('summary')?.closest('details');
        return !details || !details.querySelector('tr .row-warning, .line .row-warning, .line .history-warning, .church-comparison__line .service-warning, [data-import-pending]');
      })
      .map(marker => marker.closest<HTMLElement>('tr, .line, .church-comparison__line, details') ?? marker);
    return [...new Set(targets)];
  }
  private focusPending(target: HTMLElement, location: string, memberName: string): void {
    this.host.nativeElement.querySelectorAll('.pending-navigation-target').forEach(element => element.classList.remove('pending-navigation-target'));
    for (let ancestor: HTMLElement | null = target; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
    }
    target.classList.add('pending-navigation-target');
    target.scrollIntoView({ block: 'center', inline: 'nearest' });
    this.pendingElement = target;
    this.pendingLocation = location;
    requestAnimationFrame(() => {
      if (this.pendingElement !== target || this.pendingLocation !== location || !target.isConnected) return;
      const destination = target.matches('tr') ? target.querySelector<HTMLElement>('td:last-child')
        : target.matches('.line, .church-comparison__line') ? target.lastElementChild as HTMLElement | null : target;
      const scope = destination ?? target;
      const editorSelector = 'input:not([type="checkbox"]):not([type="hidden"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), mat-select:not([aria-disabled="true"]), [role="combobox"]';
      const controls = [...(scope.matches(editorSelector) ? [scope] : []), ...Array.from(scope.querySelectorAll<HTMLElement>(editorSelector))];
      const control = controls.find(element => element.getClientRects().length > 0);
      if (control) control.focus({ preventScroll: true });
      else { target.tabIndex = -1; target.focus({ preventScroll: true }); }
    });
    this.pendingNavigationMessage = `${memberName}: ${target.querySelector('th, strong, td')?.textContent?.trim() ?? 'Dato pendiente'}`;
    this.cdr.markForCheck();
  }
  advanceImport(): void {
    const targetId = this.missingEquivalences.length ? 'equivalence-resolver' : 'import-review';
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  equivalenceKey(item: MissingEquivalence): string { return `${item.sourceSection}|${item.sourceValue}`; }
  equivalenceTitle(item: MissingEquivalence): string { return item.sourceSection === 'organizationUnits' ? item.sourceLabel || 'Unidad sin nombre en el Portal' : item.sourceValue; }
  unitSourceIdentifier(item: MissingEquivalence): string { return item.sourceValue.startsWith('portal:') ? `ID del Portal ${item.sourceValue.slice(7)}` : `Nº ADM ${item.sourceValue}`; }
  private sourceUnitKey(record: ImportPreviewField[]): string | null | undefined {
    const field = record.find(field => field.label === 'ADM de la unidad' || field.label === 'ID del Portal de la unidad');
    return field?.sourceReference ?? (field?.label === 'ID del Portal de la unidad' && field.value ? `portal:${field.value}` : field?.value);
  }
  isEquivalenceOpen(item: MissingEquivalence): boolean { return this.activeEquivalenceKey === this.equivalenceKey(item); }
  toggleEquivalence(item: MissingEquivalence): void { this.activeEquivalenceKey = this.isEquivalenceOpen(item) ? undefined : this.equivalenceKey(item); }
  selectEquivalence(item: MissingEquivalence): void { this.activeCatalogKey = undefined; this.activeEquivalenceKey = this.equivalenceKey(item); this.showAllTargets = false; this.editingTarget = false; const draft = this.draftFor(item); this.targetSearchControl.setValue(draft?.targetId ? this.targetOptionLabel(draft, draft.targetId) : ''); }
  get pendingEquivalenceCount(): number {
    return this.reviewEquivalences.filter(item => !this.isEquivalenceValid(item)).length
      + this.reviewCatalogs.filter(catalog => !catalog.ignore && !catalog.targetId).length;
  }
  navigatePendingEquivalence(direction: 1 | -1): void {
    const entries = [
      ...this.reviewEquivalences.map(item => ({ key: this.equivalenceKey(item), pending: !this.isEquivalenceValid(item), select: () => this.selectEquivalence(item) })),
      ...this.reviewCatalogs.map(catalog => ({ key: `${catalog.kind}|${catalog.source}`, pending: !catalog.ignore && !catalog.targetId, select: () => this.selectCatalog(catalog) })),
    ];
    const currentKey = this.activeCatalogKey ?? (this.activeEquivalence ? this.equivalenceKey(this.activeEquivalence) : undefined);
    const current = entries.findIndex(entry => entry.key === currentKey);
    const start = current < 0 ? (direction === 1 ? -1 : 0) : current;
    for (let offset = 1; offset <= entries.length; offset++) {
      const entry = entries[(start + direction * offset + entries.length) % entries.length];
      if (!entry.pending) continue;
      entry.select();
      this.cdr.markForCheck();
      afterNextRender(() => {
        requestAnimationFrame(() => {
          const activeKey = this.activeCatalogKey ?? (this.activeEquivalence ? this.equivalenceKey(this.activeEquivalence) : undefined);
          if (this.activeStep !== 2 || activeKey !== entry.key) return;
          this.host.nativeElement.querySelector('.equivalence-list__item--active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          const panel = this.host.nativeElement.querySelector<HTMLElement>('.equivalence-detail');
          const draft = this.activeEquivalence ? this.draftFor(this.activeEquivalence) : undefined;
          const scope = draft?.targetId && draft.unitRule === 'specific' && !draft.targetReference
            ? panel?.querySelector('app-unit-selector') ?? panel : panel;
          const controls = Array.from(scope?.querySelectorAll<HTMLElement>('input:not([type="checkbox"]):not([disabled]):not([readonly]), mat-select:not([aria-disabled="true"]), [role="combobox"]') ?? []);
          controls.find(control => control.getClientRects().length > 0)?.focus({ preventScroll: true });
        });
      }, { injector: this.injector });
      return;
    }
  }
  selectCatalog(catalog: Catalog): void { this.activeEquivalenceKey = undefined; this.activeCatalogKey = `${catalog.kind}|${catalog.source}`; }
  setCatalogIgnored(catalog: Catalog, ignore: boolean): void { catalog.ignore = ignore; if (ignore) catalog.targetId = undefined; this.cdr.markForCheck(); }
  catalogTargetChanged(catalog: Catalog, targetId?: string): void { catalog.targetId = targetId; if (targetId) catalog.ignore = false; this.cdr.markForCheck(); }
  get activeCatalog(): Catalog | undefined { return this.remainingCatalogs.find(catalog => `${catalog.kind}|${catalog.source}` === this.activeCatalogKey); }
  get activeEquivalence(): MissingEquivalence | undefined { return this.activeCatalogKey ? undefined : this.reviewEquivalences.find(item => this.equivalenceKey(item) === this.activeEquivalenceKey) ?? this.reviewEquivalences[0]; }
  equivalenceLabel(section: string): string { return section === 'participations' ? 'Colaboraciones' : section === 'organizationUnits' ? 'Equivalencias de unidades' : 'Servicios'; }
  draftFor(item: MissingEquivalence): EquivalenceDraft { return this.equivalenceDrafts[this.equivalenceKey(item)]; }
  targetOptions(_draft: EquivalenceDraft): { id: number; name: string; targetKind: 'appointment' | 'assignment' | 'activity' }[] {
    return [
      ...this.activityOptions.map(option => ({ ...option, targetKind: 'activity' as const })),
      ...this.appointmentOptions.map(option => ({ ...option, targetKind: 'appointment' as const })),
      ...this.assignmentOptions.map(option => ({ ...option, targetKind: 'assignment' as const })),
    ];
  }
  targetKindLabel(draft: EquivalenceDraft): string { return draft.targetKind === 'appointment' ? 'Encargo' : draft.targetKind === 'assignment' ? 'Nombramiento' : 'Colaboración'; }
  targetKindLabelFor(kind: 'appointment' | 'assignment' | 'activity'): string { return kind === 'appointment' ? 'Encargo' : kind === 'assignment' ? 'Nombramiento' : 'Colaboración'; }
  targetOptionLabel(draft: EquivalenceDraft, id: number): string { const option = this.targetOptions(draft).find(item => item.id === id && item.targetKind === draft.targetKind); return option ? `${option.name} | ${this.targetKindLabel(draft)}` : ''; }
  selectedTargetOption(draft: EquivalenceDraft): { id: number; name: string; targetKind: 'appointment' | 'assignment' | 'activity' } | undefined { return draft.targetId ? this.targetOptions(draft).find(option => option.id === draft.targetId && option.targetKind === draft.targetKind) : undefined; }
  filteredTargetOptions(draft: EquivalenceDraft): { id: number; name: string; targetKind: 'appointment' | 'assignment' | 'activity' }[] { const query = this.showAllTargets ? '' : (this.targetSearchControl.value ?? '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(); return this.targetOptions(draft).filter(option => !query || option.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().includes(query)); }
  onTargetSearch(draft: EquivalenceDraft): void { this.showAllTargets = false; draft.targetId = undefined; draft.activityRoleId = undefined; }
  showAllTargetOptions(): void { this.showAllTargets = true; this.cdr.markForCheck(); }
  trackTargetOption(_index: number, option: { id: number; targetKind: string }): string { return `${option.targetKind}:${option.id}`; }
  editTarget(): void { this.editingTarget = true; this.showAllTargets = true; this.targetSearchControl.setValue(''); this.cdr.markForCheck(); }
  selectTarget(draft: EquivalenceDraft, event: MatAutocompleteSelectedEvent): void { const [kind, rawId] = String(event.option.value).split(':'); const id = Number(rawId); if (kind !== 'activity' && kind !== 'appointment' && kind !== 'assignment') return; const option = this.targetOptions(draft).find(item => item.targetKind === kind && item.id === id); if (!option) return; draft.targetKind = kind; draft.targetId = id; draft.activityRoleId = undefined; this.showAllTargets = false; this.editingTarget = false; if (kind === 'activity' && draft.unitRule === 'historical') draft.unitRule = 'portal'; this.targetSearchControl.setValue(''); this.cdr.markForCheck(); }
  applyTargetSelection(draft: EquivalenceDraft, option: { id: number; name: string; targetKind: string }): void { if (option.targetKind !== 'appointment' && option.targetKind !== 'assignment' && option.targetKind !== 'activity') return; draft.targetKind = option.targetKind; draft.targetId = option.id; draft.activityRoleId = undefined; if (option.targetKind === 'activity' && draft.unitRule === 'historical') draft.unitRule = 'portal'; this.cdr.markForCheck(); }
  activityRoles(draft: EquivalenceDraft) { return this.activities.find(item => item.id === draft.targetId)?.roles.filter(role => role.isActive) ?? []; }
  changeTargetKind(draft: EquivalenceDraft): void { draft.targetId = undefined; draft.activityRoleId = undefined; this.showAllTargets = false; this.targetSearchControl.setValue(''); this.loadTargetOptions(draft.targetKind); }
  selectTargetKind(draft: EquivalenceDraft, targetKind: EquivalenceDraft['targetKind']): void { draft.targetKind = targetKind; this.changeTargetKind(draft); }
  changeTarget(draft: EquivalenceDraft): void { draft.activityRoleId = undefined; }
  setUnitRule(draft: EquivalenceDraft, unitRule: EquivalenceDraft['unitRule']): void { draft.unitRule = unitRule; if (unitRule !== 'specific') draft.targetReference = undefined; }
  startCreateTarget(item: MissingEquivalence): void {
    const draft = this.draftFor(item);
    if (!draft || !this.createTargetDialog) return;
    draft.newTargetName = item.sourceValue; draft.newOrganizationLevelIds = [5]; draft.newRequiresActiveMinistry = false;
    this.creatingTargetItem = item;
    if (!this.areas.length) this.loadAreas();
    this.createTargetDialogRef = this.dialog.open(this.createTargetDialog, { width: '720px', maxWidth: 'calc(100vw - 32px)' });
    this.createTargetDialogRef.afterClosed().subscribe(() => {
      const creatingItem = this.creatingTargetItem;
      this.creatingTargetItem = undefined;
      if (creatingItem) this.cancelCreateTarget(creatingItem, false);
      this.cdr.markForCheck();
    });
  }
  cancelCreateTarget(item: MissingEquivalence, closeDialog = true): void {
    const draft = this.draftFor(item);
    if (draft) draft.creatingTarget = false;
    if (closeDialog) this.createTargetDialogRef?.close();
  }
  createTarget(item: MissingEquivalence): void {
    const draft = this.draftFor(item); const name = draft?.newTargetName?.trim(); if (!draft || !name || this.targetCreating || (draft.targetKind !== 'appointment' && !draft.newAreaId)) return;
    this.targetCreating = true; this.cdr.markForCheck();
    const request: Observable<unknown> = draft.targetKind === 'appointment'
      ? this.appointmentService.create(new CreateMinistryAppointmentDto({ name }))
      : draft.targetKind === 'assignment'
        ? this.assignmentService.create({ name, areaId: draft.newAreaId!, requiresActiveMinistry: !!draft.newRequiresActiveMinistry, allowedOrganizationLevelIds: draft.newOrganizationLevelIds ?? [5] })
        : this.activityService.create({ name, areaId: draft.newAreaId!, allowedOrganizationLevelIds: draft.newOrganizationLevelIds ?? [5], isActive: true, roles: [] });
    request.pipe(
      timeout(15_000),
      tap(() => this.createTargetDialogRef?.close()),
      finalize(() => {
        this.targetCreating = false;
        this.cdr.markForCheck();
      }),
    ).subscribe({ next: value => { const created = value as MinistryAppointmentDto & AssignmentDto & ActivityConfigurationDto; if (draft.targetKind === 'appointment') this.appointments = [...this.appointments, created]; else if (draft.targetKind === 'assignment') this.assignments = [...this.assignments, created]; else { this.activities = [...this.activities, created]; this.refreshServiceActivityRoles(); } this.refreshTargetOptions(draft.targetKind); draft.targetId = created.id!; draft.creatingTarget = false; this.cdr.markForCheck(); }, error: error => { this.result = error?.name === 'TimeoutError' ? 'La creación tardó demasiado. Actualizá la página antes de reintentar para verificar que el valor no se haya creado.' : this.getErrorMessage(error, 'No se pudo crear el valor en Comunidades.'); this.cdr.markForCheck(); } });
  }
  canSaveEquivalence(item: MissingEquivalence): boolean { const draft = this.draftFor(item); return !!draft && !this.equivalenceSaving[this.equivalenceKey(item)] && (draft.ignore || item.sourceSection === 'organizationUnits' ? !!draft.targetReference || draft.ignore : !!draft.targetId && (draft.unitRule !== 'specific' || !!draft.targetReference)); }
  isEquivalenceValid(item: MissingEquivalence): boolean { const draft = this.draftFor(item); return !!draft && (draft.ignore || item.sourceSection === 'organizationUnits' ? !!draft.targetReference || draft.ignore : !!draft.targetId && (draft.unitRule !== 'specific' || !!draft.targetReference)); }
  continueEquivalences(): void {
    if (this.equivalenceSubmitting) return;
    if (this.reviewCatalogs.some(catalog => !catalog.ignore && !catalog.targetId)) {
      this.equivalenceError = 'Completá u omití los catálogos pendientes para continuar.';
      this.navigatePendingEquivalence(1);
      this.cdr.markForCheck();
      return;
    }
    const invalidCount = this.equivalences.filter(item => !this.isEquivalenceValid(item)).length;
    if (invalidCount) {
      this.equivalenceError = `Completá las ${invalidCount} equivalencia${invalidCount === 1 ? '' : 's'} pendiente${invalidCount === 1 ? '' : 's'} para continuar.`;
      this.navigatePendingEquivalence(1);
      this.cdr.markForCheck();
      return;
    }
    this.equivalenceError = ''; this.equivalenceSubmitting = true;
    const items = this.equivalences.filter(item => {
      const draft = this.draftFor(item)!;
      return !draft.id || this.equivalenceOriginals[this.equivalenceKey(item)] !== this.equivalenceSignature(draft);
    });
    const saves = items.map(item => () => {
        const draft = this.draftFor(item)!;
        this.equivalenceSaving[this.equivalenceKey(item)] = true;
        const request: SavePortalImportEquivalence = { ...draft, sourceLabel: item.sourceLabel, activityRoleId: draft.targetKind === 'activity' ? draft.activityRoleId : undefined };
        return this.api.saveEquivalence(request, draft.id).pipe(
          timeout(15_000),
          tap(saved => {
            draft.id = saved.id;
            this.equivalenceOriginals[this.equivalenceKey(item)] = this.equivalenceSignature(draft);
          }),
          finalize(() => delete this.equivalenceSaving[this.equivalenceKey(item)]),
        );
      });
    for (const catalog of this.remainingCatalogs.filter(catalog => catalog.kind === 'country' && (catalog.ignore || catalog.targetId))) {
      saves.push(() => this.api.saveEquivalence({
        sourceSection: 'countries', sourceValue: catalog.source, targetKind: 'country',
        targetId: catalog.ignore ? undefined : Number(catalog.targetId),
        ignore: !!catalog.ignore, unitRule: 'portal',
      }).pipe(timeout(15_000)));
    }
    from(saves).pipe(
      concatMap(save => save()),
      finalize(() => { this.equivalenceSubmitting = false; this.cdr.markForCheck(); }),
    ).subscribe({
      complete: () => { this.missingEquivalences = []; this.activeStep = 3; this.initializeReviewChoices(); this.cdr.markForCheck(); },
      error: error => { this.equivalenceError = this.getErrorMessage(error, 'No se pudieron guardar las equivalencias. Revisá los datos e intentá nuevamente.'); this.cdr.markForCheck(); },
    });
  }
  saveEquivalence(item: MissingEquivalence): void {
    const key = this.equivalenceKey(item); const draft = this.draftFor(item); if (!draft || !this.canSaveEquivalence(item)) return;
    const request: SavePortalImportEquivalence = { ...draft, sourceLabel: item.sourceLabel, activityRoleId: draft.targetKind === 'activity' ? draft.activityRoleId : undefined };
    this.equivalenceSaving[key] = true; this.cdr.markForCheck();
    this.api.saveEquivalence(request).subscribe({ next: () => { this.missingEquivalences = this.missingEquivalences.filter(candidate => this.equivalenceKey(candidate) !== key); this.activeEquivalenceKey = this.missingEquivalences[0] ? this.equivalenceKey(this.missingEquivalences[0]) : undefined; delete this.equivalenceSaving[key]; this.cdr.markForCheck(); }, error: error => { delete this.equivalenceSaving[key]; this.result = this.getErrorMessage(error, 'No se pudo guardar la equivalencia.'); this.cdr.markForCheck(); } });
  }
  private createEquivalenceDrafts(): void { this.equivalenceDrafts = Object.fromEntries(this.equivalences.map(item => [this.equivalenceKey(item), { sourceSection: item.sourceSection, sourceValue: item.sourceValue, targetKind: item.sourceSection === 'organizationUnits' ? 'organizationUnit' : 'activity', ignore: false, unitRule: 'portal' }])); this.activeEquivalenceKey = this.equivalences[0] ? this.equivalenceKey(this.equivalences[0]) : undefined; }
  private equivalenceSignature(draft: EquivalenceDraft): string { return JSON.stringify({ targetKind: draft.targetKind, targetId: draft.targetId, targetReference: draft.targetReference, activityRoleId: draft.activityRoleId, ignore: draft.ignore, unitRule: draft.unitRule }); }
  private loadConfiguredEquivalences(): void {
    this.api.getEquivalences().subscribe({
      next: configured => this.applyConfiguredEquivalences(configured),
      error: error => { this.configuredEquivalencesLoaded = true; this.prepareReviewSteps(); this.result = this.getErrorMessage(error, 'No se pudieron cargar las equivalencias existentes.'); this.cdr.markForCheck(); },
    });
  }
  private applyConfiguredEquivalences(configured: PortalImportEquivalence[]): void {
    const bySource = new Map(configured.map(item => [`${item.sourceSection}|${this.normalizeSourceValue(item.sourceValue)}`, item]));
    for (const catalog of this.remainingCatalogs.filter(catalog => catalog.kind === 'country')) {
      const saved = bySource.get(`countries|${this.normalizeSourceValue(catalog.source)}`);
      if (!saved) continue;
      if (saved.ignore) {
        catalog.ignore = true;
        catalog.targetId = undefined;
      } else if (saved.targetKind === 'country' && catalog.options.some(option => option.id === String(saved.targetId))) {
        catalog.targetId = String(saved.targetId);
      }
    }
    for (const equivalence of this.equivalences) {
      const configuredItem = bySource.get(`${equivalence.sourceSection}|${this.normalizeSourceValue(equivalence.sourceValue)}`);
      if (!configuredItem) continue;
      this.applyConfiguredEquivalence(equivalence, configuredItem);
      const draft = this.draftFor(equivalence);
      if (draft) this.equivalenceOriginals[this.equivalenceKey(equivalence)] = this.equivalenceSignature(draft);
    }
    this.configuredEquivalencesLoaded = true;
    this.prepareReviewSteps();
    this.cdr.markForCheck();
  }
  private prepareReviewSteps(): void {
    this.inferImportCommunity();
    this.reviewEquivalences = this.equivalences.filter(item => !this.isEquivalenceValid(item));
    this.missingEquivalences = [...this.reviewEquivalences];
    this.reviewCatalogs = this.remainingCatalogs.filter(catalog => !catalog.ignore && !catalog.targetId);
    this.initialEquivalenceCount = this.reviewEquivalences.length + this.reviewCatalogs.length;
    this.activeEquivalenceKey = this.reviewEquivalences[0] ? this.equivalenceKey(this.reviewEquivalences[0]) : undefined;
    this.activeCatalogKey = !this.reviewEquivalences.length && this.reviewCatalogs[0]
      ? `${this.reviewCatalogs[0].kind}|${this.reviewCatalogs[0].source}` : undefined;
    this.activeStep = this.reviewEquivalences.length || this.reviewCatalogs.length ? 2 : 3;
    this.initializeReviewChoices();
  }
  private inferImportCommunity(): void {
    if (!this.isCommunityJson || !this.members.length) return;
    const sources = this.members.map(member => member.sourceCommunityReference?.trim());
    if (sources.some(source => !source)) return;
    const references = [...new Set(sources.map(source => this.normalizeSourceValue(source!)))];
    for (const reference of references) {
      if (this.communityDestinations[reference]) continue;
      const draft = this.findEquivalenceDraft('organizationUnits', reference);
      if (!draft || draft.ignore || draft.targetKind !== 'organizationUnit' || !draft.targetReference) continue;
      const community = this.communityOptionsList.find(unit => unit.id.toLowerCase() === draft.targetReference!.trim().toLowerCase());
      if (community) {
        this.communityDestinations[reference] = community.id;
        if (references.length === 1 && !this.importCommunityId) { this.importCommunityId = community.id; this.detectedImportCommunityId = community.id; }
      }
    }
  }
  private normalizeSourceValue(value: string): string { return value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(); }
  private importedMinistryEventType(value: string): number {
    const normalized = this.normalizeSourceValue(value);
    const aliases: Record<string, number> = {
      renuncia: 7, 'renuncia ministerial': 7,
      relevacion: 8, 'relevo ministerial': 8, cese: 8,
      'confirmacion en el ministerio': 10, 'confirmacion ministerial': 10,
    };
    if (aliases[normalized]) return aliases[normalized];
    return value === 'Ordenación' ? 1
      : value === 'Reintegro luego de una licencia' ? 3
        : value === 'Licencia ministerial' ? 4
          : value === 'Puesta en descanso' ? 9
            : 0;
  }
  private findEquivalenceDraft(sourceSection: string, sourceValue: string): EquivalenceDraft | undefined {
    const normalizedValue = this.normalizeSourceValue(sourceValue);
    return Object.values(this.equivalenceDrafts).find(draft =>
      draft.sourceSection === sourceSection && this.normalizeSourceValue(draft.sourceValue) === normalizedValue);
  }
  private applyConfiguredEquivalence(item: MissingEquivalence, configured: PortalImportEquivalence): void {
    const draft = this.draftFor(item);
    if (!draft) return;
    draft.id = configured.id; draft.targetKind = configured.targetKind === 'ignore' ? draft.targetKind : configured.targetKind as EquivalenceDraft['targetKind'];
    draft.targetId = configured.targetId; draft.targetReference = configured.targetReference; draft.activityRoleId = configured.activityRoleId;
    draft.ignore = configured.ignore; draft.unitRule = configured.unitRule as EquivalenceDraft['unitRule'];
  }
  private loadTargetOptions(kind: EquivalenceDraft['targetKind']): void {
    if (kind === 'appointment' && this.appointments.length) { this.refreshTargetOptions(kind); return; }
    if (kind === 'assignment' && this.assignments.length) { this.refreshTargetOptions(kind); return; }
    if (kind === 'activity' && this.activities.length) { this.refreshTargetOptions(kind); return; }
    const request: Observable<unknown> = kind === 'appointment' ? this.appointmentService.getAll() : kind === 'assignment' ? this.assignmentService.getAll() : this.activityService.getAll();
    request.subscribe({ next: value => { if (kind === 'appointment') this.appointments = value as MinistryAppointmentDto[]; else if (kind === 'assignment') this.assignments = value as AssignmentDto[]; else { this.activities = value as ActivityConfigurationDto[]; this.refreshServiceActivityRoles(); } this.refreshTargetOptions(kind); this.cdr.markForCheck(); }, error: error => { this.result = this.getErrorMessage(error, 'No se pudieron cargar las opciones para las equivalencias.'); this.cdr.markForCheck(); } });
  }
  private refreshServiceActivityRoles(): void {
    this.serviceActivityRoles = this.activities.flatMap(activity => activity.roles.filter(role => role.isActive).map(role => ({ activityId: activity.id, id: role.id, name: role.name, isDefault: role.isDefault })));
  }
  private refreshTargetOptions(kind: EquivalenceDraft['targetKind']): void {
    if (kind === 'appointment') this.appointmentOptions = this.appointments.filter(item => item.isActive !== false).map(item => ({ id: item.id!, name: item.name! }));
    else if (kind === 'assignment') this.assignmentOptions = this.assignments.filter(item => item.isActive !== false).map(item => ({ id: item.id!, name: item.name! }));
    else this.activityOptions = this.activities.filter(item => item.isActive).map(item => ({ id: item.id, name: item.name }));
  }
  private loadAreas(): void { this.areaService.getActive().subscribe({ next: areas => { this.areas = areas; this.cdr.markForCheck(); }, error: error => { this.result = this.getErrorMessage(error, 'No se pudieron cargar las áreas para crear el valor.'); this.cdr.markForCheck(); } }); }

  import(): void {
    if (!this.canImport) return;
    this.importSummary = undefined;
    this.loading = true; this.importing = true; this.result = ''; this.memberResults = [];
    this.importProgress = { percent: 0, stage: 'Preparando importación', processedMembers: 0, totalMembers: this.selectedMemberCount };
    this.cdr.markForCheck();
    const selections = this.members.map(m => ({ portalMemberId: m.portalMemberId, ...this.selections[m.portalMemberId], community: this.memberImportCommunity(m) }));
    const catalogMappings = this.remainingCatalogs.map(c => ({ kind: c.kind, source: c.source, targetId: c.targetId }));
    this.api.import({ sourceJson: this.isCommunityJson ? undefined : this.sourceJson, sourceFileName: this.isCommunityJson ? undefined : this.fileName, batchId: this.batchId,
      photos: this.photoSelections, selections, catalogMappings }, this.isCommunityJson, progress => {
        this.importProgress = progress;
        this.cdr.markForCheck();
      }).pipe(finalize(() => {
        this.loading = false; this.importing = false; this.cdr.markForCheck();
      })).subscribe({
      next: response => {
        this.importSummary = { created: response.created, skipped: response.skipped, warnings: response.warnings ?? [] };
        this.result = '';
        this.memberResults = response.members ?? []; this.loading = false; this.cdr.markForCheck();
      }, error: error => this.fail(error, 'La importación no pudo completarse.')
    });
  }

  restorePerson(item: ImportMemberResult): void {
    if (!item.personId || this.restoringPersonId) return;
    this.restoringPersonId = item.personId;
    this.personService.restorePerson(item.personId).subscribe({
      next: () => {
        item.status = 'Restaurado';
        item.messages = ['La ficha fue restaurada. Confirmá nuevamente la importación para completar los datos pendientes.'];
        this.importSummary = undefined;
        this.result = item.messages[0];
        this.activeStep = 4;
        this.restoringPersonId = undefined;
        this.cdr.markForCheck();
      },
      error: error => {
        this.result = this.getErrorMessage(error, 'No se pudo restaurar la ficha.');
        this.restoringPersonId = undefined;
        this.cdr.markForCheck();
      }
    });
  }

  private fail(error: unknown, fallback: string): void {
    const status = (error as { status?: number })?.status;
    if (this.importing && (status === 408 || status === 504 || (error as { name?: string })?.name === 'TimeoutError')) {
      this.result = 'Se agotó el tiempo de espera de la importación. Revisá el historial del lote antes de volver a confirmarla.';
      this.loading = false; this.cdr.markForCheck(); return;
    }
    const problem = (error as { error?: { detail?: string; errors?: Record<string, string[]> } })?.error;
    const details = problem?.errors ? Object.entries(problem.errors).map(([field, messages]) =>
      messages.map(message => message.includes('(ID Portal ') ? message : `${field}: ${message}`).join('\n')).join('\n') : '';
    this.result = details || problem?.detail || fallback; this.loading = false; this.cdr.markForCheck();
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const problem = (error as { error?: { detail?: string; title?: string }; message?: string })?.error;
    return problem?.detail || problem?.title || (error as { message?: string })?.message || fallback;
  }
}
